import { useState, useRef, useEffect, useMemo } from 'react';
import styles from './ChatBot.module.css';
import { OwcIcon } from '@one/react';

const AGENT_API = process.env.REACT_APP_AGENT_API;

const SUGGESTED_QUESTIONS = [
  'What is DLI?',
  'List all Data Groups.',
  'What events are in the Lab Data Group?',
  'Show me the latest Lab Release Version.',
];

// ─── Markdown / rendering helpers ──────────────────────────────────────────

function renderInline(text) {
  return text
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');
}

function renderMarkdownTable(tableBlock) {
  const rows = tableBlock
    .trim()
    .split('\n')
    .filter((r) => r.trim());
  if (rows.length < 2) return renderText(tableBlock);
  const parseRow = (row) =>
    row
      .split('|')
      .map((c) => c.trim())
      .filter((_, i, a) => i > 0 && i < a.length);
  const headers = parseRow(rows[0]);
  const dataRows = rows.slice(2);
  const ths = headers.map((h) => `<th>${renderInline(h)}</th>`).join('');
  const trs = dataRows
    .map(
      (r) =>
        `<tr>${parseRow(r)
          .map((c) => `<td>${renderInline(c)}</td>`)
          .join('')}</tr>`
    )
    .join('');
  return `<table><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table>`;
}

function stripJsoncComments(src) {
  return src.replace(/\/\*[\s\S]*?\*\//g, '').replace(/\/\/.*/g, '');
}

function escapeHtml(str) {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function highlightJson(jsonStr) {
  return escapeHtml(jsonStr).replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    (match) => {
      let cls = 'json-number';
      if (/^"/.test(match)) cls = /:$/.test(match) ? 'json-key' : 'json-string';
      else if (/true|false/.test(match)) cls = 'json-bool';
      else if (/null/.test(match)) cls = 'json-null';
      return `<span class="${cls}">${match}</span>`;
    }
  );
}

function renderCodeBlock(lang, code) {
  if (lang === 'json' || lang === 'jsonc') {
    const raw = lang === 'jsonc' ? stripJsoncComments(code) : code;
    let formatted = code;
    try {
      formatted = JSON.stringify(JSON.parse(raw.trim()), null, 2);
    } catch {
      formatted = code;
    }
    return `<pre class="chat-code-block chat-json-block"><code>${highlightJson(formatted)}</code></pre>`;
  }
  return `<pre class="chat-code-block"><code>${escapeHtml(code)}</code></pre>`;
}

function renderText(text) {
  return text
    .replace(/^#### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^\* /gm, '&bull; ')
    .replace(/\n/g, '<br/>');
}

function renderMarkdown(text) {
  if (!text) return '';

  const codeBlockRegex = /```(\w*)\n([\s\S]*?)```/g;
  const tableRegex = /((?:^|\n)\|.+\|(?:\n\|[\s:|-]+\|)(?:\n\|.+\|)+)/g;

  // Split on fenced code blocks first
  const rawParts = [];
  let lastIndex = 0;
  let match;

  while ((match = codeBlockRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      rawParts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    }
    rawParts.push({ type: 'code', lang: match[1], content: match[2] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    rawParts.push({ type: 'text', content: text.slice(lastIndex) });
  }

  // For each text part, extract markdown tables
  const parts = [];
  for (const part of rawParts) {
    if (part.type === 'code') {
      parts.push(renderCodeBlock(part.lang, part.content));
      continue;
    }
    tableRegex.lastIndex = 0;
    let tLast = 0;
    let tMatch;
    while ((tMatch = tableRegex.exec(part.content)) !== null) {
      if (tMatch.index > tLast) {
        parts.push(renderText(part.content.slice(tLast, tMatch.index)));
      }
      parts.push(renderMarkdownTable(tMatch[1]));
      tLast = tMatch.index + tMatch[0].length;
    }
    if (tLast < part.content.length) {
      parts.push(renderText(part.content.slice(tLast)));
    }
  }

  return parts.join('');
}

// ─── Component ─────────────────────────────────────────────────────────────

function generateId() {
  return Math.random().toString(36).slice(2);
}

export default function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [toolStatus, setToolStatus] = useState('');
  const [sessionId] = useState(() => crypto.randomUUID());
  const [size, setSize] = useState({ width: 400, height: 520 });

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const resizing = useRef(false);
  const startPos = useRef({ x: 0, y: 0, w: 0, h: 0 });

  // Global mouse listeners for resize drag
  useEffect(() => {
    const onMouseMove = (e) => {
      if (!resizing.current) return;
      const dw = startPos.current.x - e.clientX;
      const dh = startPos.current.y - e.clientY;
      setSize({
        width: Math.max(300, Math.min(window.innerWidth * 0.9, startPos.current.w + dw)),
        height: Math.max(350, Math.min(window.innerHeight * 0.8, startPos.current.h + dh)),
      });
    };
    const onMouseUp = () => {
      resizing.current = false;
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, toolStatus]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  async function sendMessage(overrideText) {
    const text = (overrideText !== undefined ? overrideText : input).trim();
    if (!text || isStreaming) return;

    if (!AGENT_API) {
      console.warn('REACT_APP_AGENT_API is not set');
      setMessages((prev) => [
        ...prev,
        {
          id: generateId(),
          role: 'assistant',
          content: 'Chat agent URL is not configured. Set REACT_APP_AGENT_API.',
        },
      ]);
      return;
    }

    setInput('');
    const assistantMsgId = generateId();
    setMessages((prev) => [
      ...prev,
      { id: generateId(), role: 'user', content: text },
      { id: assistantMsgId, role: 'assistant', content: '', isStreaming: true },
    ]);
    setIsStreaming(true);
    setToolStatus('');

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(`${AGENT_API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, session_id: sessionId }),
        signal: controller.signal,
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let event;
          try {
            event = JSON.parse(line.slice(6));
          } catch {
            continue;
          }
          const { type, content, tool, message } = event;
          if (type === 'token') {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId ? { ...m, content: m.content + content } : m
              )
            );
          } else if (type === 'tool_use') {
            setToolStatus(`Searching via ${tool}…`);
          } else if (type === 'done') {
            setToolStatus('');
            setMessages((prev) =>
              prev.map((m) => (m.id === assistantMsgId ? { ...m, isStreaming: false } : m))
            );
          } else if (type === 'error') {
            setToolStatus('');
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId
                  ? { ...m, content: message || 'An error occurred.', isStreaming: false }
                  : m
              )
            );
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgId
            ? { ...m, content: `Error: ${err.message}`, isStreaming: false }
            : m
        )
      );
    } finally {
      setIsStreaming(false);
      setToolStatus('');
      abortRef.current = null;
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  function handleClose() {
    abortRef.current?.abort();
    setIsOpen(false);
  }

  const hasCompletedResponse = useMemo(
    () => messages.some((m) => m.role === 'assistant' && m.content && !m.isStreaming),
    [messages]
  );

  function handleFabClick() {
    if (isOpen) {
      handleClose();
    } else {
      setIsOpen(true);
    }
  }

  return (
    <>
      {/* Chat panel */}
      <div
        className={`${styles.panel}${isOpen ? '' : ` ${styles.panelHidden}`}${isMaximized ? ` ${styles.panelMaximized}` : ''}`}
        role="dialog"
        aria-label="Ask DLI Agent"
        aria-hidden={!isOpen}
        style={isMaximized ? undefined : { width: size.width, height: size.height }}
      >
        {/* Resize grip — drag from top-left to grow the panel */}
        {!isMaximized && (
          <div
            className={styles.resizeHandle}
            onMouseDown={(e) => {
              e.preventDefault();
              resizing.current = true;
              startPos.current = { x: e.clientX, y: e.clientY, w: size.width, h: size.height };
            }}
          />
        )}

        <div className={styles.header}>
          <span className={styles.headerTitle}>🤖 Ask DLI Agent</span>
          <div className={styles.headerActions}>
            <button
              className={styles.headerBtn}
              onClick={() => setIsMaximized((m) => !m)}
              aria-label={isMaximized ? 'Restore chat' : 'Maximize chat'}
              title={isMaximized ? 'Restore' : 'Maximize'}
            >
              {isMaximized ? '🗗' : '🗖'}
            </button>
            <button
              className={styles.headerBtn}
              onClick={handleClose}
              aria-label="Close chat"
              title="Close"
            >
              ✕
            </button>
          </div>
        </div>

        <div className={styles.messages}>
          {messages.length === 0 && (
            <div className={styles.suggestions}>
              <p className={styles.suggestionsHint}>
                Ask me anything about DLI&apos;s Events, Resources or Schemas.
              </p>
              {SUGGESTED_QUESTIONS.map((q) => (
                <button key={q} className={styles.suggestionBtn} onClick={() => sendMessage(q)}>
                  {q}
                </button>
              ))}
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.id}
              className={msg.role === 'user' ? styles.msgUser : styles.msgAssistant}
            >
              {msg.role === 'assistant' ? (
                <>
                  {/* Thinking / tool-call indicator — shown before first token */}
                  {msg.isStreaming && !msg.content && (
                    <span className={styles.thinking}>
                      <span className={styles.thinkingLabel}>
                        {toolStatus ? `🔧 ${toolStatus}` : 'Thinking'}
                      </span>
                      <span className={styles.thinkingDots}>
                        <span />
                        <span />
                        <span />
                      </span>
                    </span>
                  )}
                  {/* Tool call badge while content is also streaming */}
                  {msg.isStreaming && msg.content && toolStatus && (
                    <div className={styles.toolCall}>🔧 {toolStatus}</div>
                  )}
                  {/* Streamed / final content */}
                  {msg.content && (
                    <span dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />
                  )}
                </>
              ) : (
                msg.content
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
        {hasCompletedResponse && (
          <div className={styles.disclaimer} role="note">
            <span className={styles.disclaimerIcon} aria-hidden="true">
              <OwcIcon
                slot="suffix"
                name="circle_info"
                family="outlined"
                className={styles.disclaimerIcon}
              />
            </span>
            AI Can Hallucinate. Please verify information !
          </div>
        )}

        <div className={styles.inputRow}>
          <input
            ref={inputRef}
            className={styles.input}
            type="text"
            placeholder="Type a message…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isStreaming}
            aria-label="Chat input"
          />
          <button
            className={styles.sendBtn}
            onClick={() => sendMessage()}
            disabled={isStreaming || !input.trim()}
            aria-label="Send message"
          >
            ➤
          </button>
        </div>
      </div>

      {/* FAB toggle */}
      <button
        className={styles.fab}
        onClick={handleFabClick}
        aria-label={isOpen ? 'Close chat' : 'Open Ask DLI Agent'}
        aria-expanded={isOpen}
        title="Chat with DLI Agent"
      >
        {isOpen ? '✕' : '🤖'}
      </button>
    </>
  );
}
